import React, { useCallback } from 'react';
import { Download } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { useWavesurfer } from '@wavesurfer/react';

const PlayFilled = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4" fill="currentColor">
    <path d="M8 5v14l11-7-11-7Z" />
  </svg>
);
const PauseFilled = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4" fill="currentColor">
    <path d="M7 5h4v14H7zM13 5h4v14h-4z" />
  </svg>
);

interface AudioWavePlayerProps {
  url: string;
  height?: number;
  waveColor?: string;
  progressColor?: string;
}

const resolveColor = (token: string) => {
  if (typeof window === 'undefined') return token;
  // Direct hex or rgb/hsl already
  if (/^#|^rgb|^hsl/.test(token)) return token;
  // CSS variable usage
  const varMatch = token.match(/var\(--([a-zA-Z0-9-]+)\)/);
  if (varMatch) {
    const cssVal = getComputedStyle(document.documentElement)
      .getPropertyValue(`--${varMatch[1]}`)
      .trim();
    if (cssVal) {
      if (token.startsWith('hsl(')) return `hsl(${cssVal})`;
      if (token.startsWith('hsla(')) return `hsla(${cssVal})`;
      return cssVal;
    }
  }
  // Tailwind utility: create temp element to read computed style (bg-*, text-*, ring-*)
  if (/^(bg|text|ring|border)-/.test(token)) {
    const probe = document.createElement('div');
    probe.className = token + ' hidden fixed -z-50 w-0 h-0';
    document.body.appendChild(probe);
    const style = getComputedStyle(probe);
    // Try multiple properties depending on token prefix
    const color =
      (/^text-/.test(token)
        ? style.color
        : /^ring-/.test(token)
          ? style.getPropertyValue('--tw-ring-color') || style.boxShadow
          : style.backgroundColor) || '';
    document.body.removeChild(probe);
    if (color && color !== 'transparent' && color !== 'rgba(0, 0, 0, 0)') {
      return color;
    }
  }
  return token;
};

const formatTime = (sec: number) => {
  if (!Number.isFinite(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const AudioWavePlayer: React.FC<AudioWavePlayerProps> = ({
  url,
  height = 64,
  waveColor = 'bg-muted',
  progressColor = 'bg-primary',
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const { wavesurfer, isReady, isPlaying, currentTime } = useWavesurfer({
    container: containerRef,
    url,
    height,
    waveColor: resolveColor(waveColor),
    progressColor: resolveColor(progressColor),
    cursorColor: resolveColor('hsl(var(--primary))'),
    barWidth: 1.5,
    cursorWidth: 1.5,
  });

  const onPlayPause = useCallback(() => {
    if (wavesurfer) {
      wavesurfer.playPause();
    }
  }, [wavesurfer]);

  const handleDownload = () => {
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = 'call-recording.ogg';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      // silent fail
    }
  };

  return (
    <div className="space-y-2">
      <div ref={containerRef} />
      <div className="flex items-center gap-3 text-xs">
        <Button
          type="button"
          size="icon"
          onClick={onPlayPause}
          disabled={!isReady}
          aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
          className="gap-2 size-8"
        >
          {isPlaying ? (
            <>
              <PauseFilled />
              <span className="sr-only">Pause</span>
            </>
          ) : (
            <>
              <PlayFilled />
              <span className="sr-only">Play</span>
            </>
          )}
        </Button>
        <div className="tabular-nums text-muted-foreground">
          {formatTime(currentTime)} / {formatTime(wavesurfer?.getDuration() || 0)}
        </div>
        <div className="ml-auto">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleDownload}
            disabled={!isReady}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            <span className="sm:inline">Download audio</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
